import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Skeleton,
  Alert,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import { dashboardAPI } from '../../services/dashboard-api';
import { formatHours } from '../../utils/formatters';

const MetricRow = ({ label, value, cohort, cohortSample, threshold = 20 }) => {
  const suppressCohort = cohort == null || cohortSample < threshold;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
        {cohort !== undefined && (
          suppressCohort ? (
            <Tooltip title={`Not enough partner data (${cohortSample || 0} clicks, minimum ${threshold})`} arrow>
              <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help', borderBottom: '1px dashed' }}>
                Partners avg: —
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Partners avg: {cohort}
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
};

const SectionPanel = ({ title, data, showCohort = true }) => {
  if (!data) return null;

  const convPct = data.conversion_pct != null ? `${data.conversion_pct}%` : '—';
  const cohortConv = showCohort && data.cohort_conversion_pct != null ? `${data.cohort_conversion_pct}%` : null;
  const p50 = formatHours(data.p50_conversion_hours);
  const cohortP50 = showCohort && data.cohort_p50_conversion_hours != null ? formatHours(data.cohort_p50_conversion_hours) : null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      <MetricRow label="Clicks" value={(data.clicks || 0).toLocaleString()} />
      <MetricRow label="Orders" value={(data.orders || 0).toLocaleString()} />
      <MetricRow
        label="Conversion rate"
        value={convPct}
        cohort={cohortConv}
        cohortSample={data.cohort_sample_clicks || 0}
      />
      <MetricRow
        label="Typical order time"
        value={p50}
        cohort={cohortP50}
        cohortSample={data.cohort_sample_clicks || 0}
      />

      {data.top_products?.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Top products
          </Typography>
          {data.top_products.map((p, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
              <Typography variant="caption" noWrap sx={{ maxWidth: '75%' }}>
                {p.product_name}{p.color ? ` · ${p.color}` : ''}
              </Typography>
              <Chip label={p.order_count} size="small" sx={{ height: 18, fontSize: 11 }} />
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

const PartnerDrilldownModal = ({ open, partner, onClose, startDate, endDate, myRetailerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!partner?.partner_id) return;
    try {
      setLoading(true);
      setError('');
      const response = await dashboardAPI.getPartnerDrilldown(partner.partner_id, {
        start_date: startDate?.toISOString(),
        end_date: endDate?.toISOString(),
        my_retailer_id: myRetailerId || undefined,
      });
      if (response.success) {
        setData(response.data);
      } else {
        setError('Failed to load partner data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [partner?.partner_id, startDate, endDate, myRetailerId]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  const partnerName = data?.partner_name || partner?.partner_name || 'Partner';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{partnerName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2].map((i) => (
              <Grid size={{ xs: 12, md: 6 }} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !data ? null : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionPanel
                title={`Received from ${partnerName}`}
                data={data.received}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionPanel
                title={`Sent to ${partnerName}`}
                data={data.sent}
                showCohort={false}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerDrilldownModal;
